import { FC, useMemo, useState } from 'react';
import {
  Button,
  Card,
  DateRangePicker,
  RadixTable,
  TColumn,
} from '@features/ui';
import { DateValueType } from '@lib/utils.ts';
import {
  TPayoutsPaAnalysisByDateTable,
  TPaPayoutAnalysisRes,
} from '@src/types';
import { getPaPayoutAnalysis } from '@src/services/reports';
import { format } from 'date-fns';

interface PayoutsPaAnalysisByDateTableProps {
  valueStart: DateValueType;
  setValueStart: (value: DateValueType) => void;
  valueEnd: DateValueType;
  setValueEnd: (value: DateValueType) => void;
}

export const PayoutsPaAnalysisByDateTable: FC<
  PayoutsPaAnalysisByDateTableProps
> = ({ valueStart, setValueStart, valueEnd, setValueEnd }) => {
  const [data, setData] = useState<TPayoutsPaAnalysisByDateTable[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGet = async () => {
    if (!valueStart?.startDate || !valueEnd?.startDate) {
      setError('Please select both start and end dates');

      return;
    }

    try {
      setLoading(true);
      setError(null);
      const fromDate = format(valueStart.startDate, 'yyyy-MM-dd');
      const toDate = format(valueEnd.startDate, 'yyyy-MM-dd');

      const response: TPaPayoutAnalysisRes = await getPaPayoutAnalysis({
        from_date: fromDate,
        to_date: toDate,
      });

      setData(response.detail_rows);
    } catch (err) {
      setError('Failed to fetch data. Please try again.');
      console.error('Error fetching PA payout analysis:', err);
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(
    (): TColumn<TPayoutsPaAnalysisByDateTable>[] => [
      {
        title: 'Package',
        render: (item) => <p>{item?.package ?? '-'}</p>,
      },
      {
        title: `Total PA Accounts`,
        className: 'text-center',
        render: (item) => <p>{item?.total_pa_accounts ?? '-'}</p>,
      },
      {
        title: `Total Payouts`,
        className: 'text-center',
        render: (item) => <p>{item?.total_payouts ?? '-'}</p>,
      },
      {
        title: `Accounts 30 Days+`,
        className: 'text-center',
        render: (item) => <p>{item?.accounts_30_days ?? '-'}</p>,
      },
      {
        title: `Users 30 days+`,
        className: 'text-center',
        render: (item) => <p>{item?.users_30_days ?? '-'}</p>,
      },
      {
        title: `Avg PnL 30 days`,
        className: 'text-center',
        render: (item) => <p>${item?.avg_pnl_30_days ?? '0.00'}</p>,
      },
      {
        title: `Accounts 60 days+`,
        className: 'text-center',
        render: (item) => <p>{item?.accounts_60_days ?? '-'}</p>,
      },
      {
        title: 'Users 60 days+',
        className: 'text-center',
        render: (item) => <p>{item?.users_60_days ?? '-'}</p>,
      },
      {
        title: 'Avg PnL 60 days',
        className: 'text-center',
        render: (item) => <p>${item?.avg_pnl_60_days ?? '0.00'}</p>,
      },
      {
        title: 'Accounts 90 days+',
        className: 'text-center',
        render: (item) => <p>{item?.accounts_90_days ?? '-'}</p>,
      },
      {
        title: 'Users 90 days+',
        className: 'text-center',
        render: (item) => <p>{item?.users_90_days ?? '-'}</p>,
      },
      {
        title: 'Avg PnL 90 days',
        className: 'text-center',
        render: (item) => <p>${item?.avg_pnl_90_days ?? '0.00'}</p>,
      },
      {
        title: 'Accounts 120 days+',
        className: 'text-center',
        render: (item) => <p>{item?.accounts_120_days ?? '-'}</p>,
      },
      {
        title: 'Users 120 days+',
        className: 'text-center',
        render: (item) => <p>{item?.users_120_days ?? '-'}</p>,
      },
      {
        title: 'Avg PnL 120 days',
        className: 'text-center',
        render: (item) => <p>${item?.avg_pnl_120_days ?? '0.00'}</p>,
      },
      {
        title: 'Accounts 150 days+',
        className: 'text-center',
        render: (item) => <p>{item?.accounts_150_days ?? '-'}</p>,
      },
      {
        title: 'Users 150 days+',
        className: 'text-center',
        render: (item) => <p>{item?.users_150_days ?? '-'}</p>,
      },
      {
        title: 'Avg PnL 150 days',
        className: 'text-center',
        render: (item) => <p>${item?.avg_pnl_150_days ?? '0.00'}</p>,
      },
      {
        title: `Payout Per Month`,
        className: 'text-center',
        render: (item) => <p>${item?.payout_per_month ?? '0.00'}</p>,
      },
      {
        title: `Max Payout Per Account`,
        className: 'text-center',
        render: (item) => <p>${item?.payout_max_account ?? '0.00'}</p>,
      },
      {
        title: `Total Accounts`,
        className: 'text-center',
        render: (item) => <p>{item?.total_accounts ?? '-'}</p>,
      },
      {
        title: `Accounts to Payout Ratio`,
        className: 'text-center',
        render: (item) => <p>{item?.accounts_to_payout_ratio ?? '0.00'}%</p>,
      },
    ],
    []
  );

  return (
    <Card className={'mt-4.5'}>
      <div
        className={
          'flex lg:flex-row flex-col justify-between items-start mb-5 gap-6'
        }
      >
        <div className={'flex gap-6 flex-wrap'}>
          <DateRangePicker
            asSingle={true}
            className={'max-w-[239px]'}
            placeholder={'Start Date'}
            size={'sm'}
            value={valueStart}
            variant={'outline'}
            onChange={setValueStart}
          />
          <DateRangePicker
            asSingle={true}
            className={'max-w-[239px]'}
            placeholder={'End Date'}
            size={'sm'}
            value={valueEnd}
            variant={'outline'}
            onChange={setValueEnd}
          />
        </div>
        <div className={'flex gap-5'}>
          <Button
            disabled={loading}
            size={'sm'}
            variant={'lime'}
            onClick={handleGet}
          >
            {loading ? 'Loading...' : 'Get'}
          </Button>
        </div>
      </div>
      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-600 rounded">{error}</div>
      )}
      <RadixTable columns={columns} data={data} />
    </Card>
  );
};
